import { useQuery } from '@tanstack/react-query';

import { sessionsService } from '../services';
import type { ClinicalBundle } from '../types';

import { sessionsKeys } from './useSessions';

export interface UseClinicalBundleResult {
  bundle: ClinicalBundle | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

/**
 * Fetch the post-session clinical bundle. Disabled when `sessionId` is null
 * (lets the post-session UI declare its hook unconditionally while the
 * drawer hasn't picked a session yet).
 *
 * Cache policy: 5-minute freshness, 30-minute retention. All four
 * SessionPanel tabs (overview/notes/transcript/case-reports) call this
 * hook, but only one tab is mounted at a time — without retention the
 * cache was purged on every tab switch, triggering a refetch on switch-back.
 *
 * Liveness still works via explicit refetches: SessionNotesTab calls
 * refetch on the SSE `done` event, and RecordingsPlayer calls it from
 * the <audio> element's onError (presign expired / S3 byte-range 403).
 */
export function useClinicalBundle(
  sessionId: string | null,
  viewMode?: string,
): UseClinicalBundleResult {
  const query = useQuery({
    // viewMode is part of the cache key so the read-only History bundle
    // (BE may strip / mask fields the viewer can't see) doesn't collide
    // with the editable sessions-workspace bundle for the same sessionId.
    queryKey: sessionsKeys.operation(`clinical-bundle:${viewMode ?? ''}`, sessionId ?? ''),
    queryFn: () => {
      if (!sessionId) throw new Error('missing sessionId');
      return sessionsService.getClinicalBundle(sessionId, viewMode);
    },
    enabled: sessionId !== null && sessionId !== '',
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    bundle: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
